import {
  create,
  emptyPost,
  emptyPostWithResponse,
  get,
  getBlob,
  getItem,
  getItems,
  getQueryItems,
  update,
  updateItem
} from "../Api";
import { QuoteQueryParams } from "./QuoteQueryParams";
import { LocalHeroesQuote, Quote, QuoteBalance, QuoteSearchResponse } from "./Quote";
import { UpdateQuoteForm } from "./UpdateQuoteForm";
import { EQuoteStatus } from "./EQuoteStatus";
import { SolarReport } from "./SolarReport";
import { PaymentIntentRequest, PaymentIntentResponse } from "./Payment";
import { FinanceEstimateResponse } from "./FinanceEstimateResponse";
import { EQuotePurchaserType } from "../Types";
import { Lead } from "./Lead";

export async function searchQuotes(queryParams: QuoteQueryParams, signal: AbortSignal): Promise<QuoteSearchResponse[]> {
  const { searchQuery, page, order } = queryParams;

  var queryString = `?page=${page ?? 1}`;

  if (searchQuery) queryString += `&query=${searchQuery}`;
  if (order || order === 0) queryString += `&order=${order!.toString()}`;

  return getQueryItems<QuoteSearchResponse>("Store/Quote/Search", queryString, signal);
}

export async function getUserQuotes(userId: string): Promise<Quote[]> {
  return getItems<Quote>(`Store/Quote/User/${userId}`);
}

export async function getRelatedQuotes(quoteId: string): Promise<Quote[]> {
  return getQueryItems<Quote>(`Store/Quote/${quoteId}/Related`, "");
}

export async function getQuote(id: string) {
  var quote = await getItem<Quote>("Store/Quote", id);
  return quote;
}

export async function updateQuoteStatus(quoteId: string, status: EQuoteStatus) {
  var path = `Store/Quote/${quoteId}`;
  var body = {
    status
  } as Quote;
  return update<Quote>(path, body);
}

export async function updateQuotePurchaserType(quoteId: string, purchaserType: EQuotePurchaserType) {
  var path = `store/quote/${quoteId}/purchaser-type`;
  var body = {
    purchaserType
  } as Quote;
  return update<Quote>(path, body);
}

export async function updateQuoteLeadSource(quoteId: string, leadSource: string) {
  var quoteForm = {
    id: quoteId,
    leadSource
  } as UpdateQuoteForm;
  return updateQuote(quoteForm);
}

export async function updateQuote(quote: UpdateQuoteForm) {
  return updateItem<UpdateQuoteForm, Quote>("Store/Quote", quote);
}

export async function applyPromoCode(quoteId: string, promoCode: string) {
  return emptyPost(`store/quotediscount/${quoteId}/apply/${promoCode}`);
}

export async function removePromoCode(quoteId: string, quoteDuductionId: string) {
  return emptyPost(`store/quotediscount/${quoteId}/remove/${quoteDuductionId}`);
}

export async function syncQuoteDeduction(quoteId: string, autoApply: boolean) {
  return await getItem<Quote>(`store/quote/${quoteId}/deduction/sync/${autoApply}`, "");
}

export async function quoteViewedCheckout(quoteId: string) {
  return await emptyPost(`store/quote/view-checkout/${quoteId}`);
}

export async function downloadQuotePdf(quoteId: string) {
  return await getBlob(`store/email/quote/${quoteId}/pdf`);
}

export async function getSolarReport(assessmentId: string, netCost: number) {
  return await getItem<SolarReport>(`assessments/solarreport/assessment`, `${assessmentId}/${netCost}`);
}

export async function getSolarReportQuote({
  quoteId,
  includeBattery = false,
  includeFinance = false
}: {
  quoteId: string;
  includeBattery?: boolean;
  includeFinance?: boolean;
}) {
  return await getItem<SolarReport>(
    `assessments/solarreport/quote`,
    `${quoteId}?includeBattery=${includeBattery}&includeFinance=${includeFinance}`
  );
}

export async function getSolarReportOutcome({
  leadId,
  productId,
  includeFinance = false
}: {
  leadId: string;
  productId?: string | null;
  includeFinance?: boolean;
}) {
  var path = `assessments/solarreport/lead/${leadId}`;
  if (productId) {
    path += `/product/${productId}`;
  }

  return await getItem<SolarReport>(path, `?includeFinance=${includeFinance}`);
}

export async function createDepositPaymentIntent(quoteId: string) {
  var response = await emptyPostWithResponse<PaymentIntentResponse>(`store/payment/quote/${quoteId}/deposit-intent`);
  return response.clientSecret;
}

export async function createPaymentIntent(quoteId: string, amount: number | null = null) {
  if (!amount) {
    return await emptyPostWithResponse<PaymentIntentResponse>(`store/payment/quote/${quoteId}/payment-intent`);
  }
  return await create<PaymentIntentRequest, PaymentIntentResponse>(`store/payment/quote/${quoteId}/payment-intent`, {
    amount
  } as PaymentIntentRequest);
}

export async function toggleGraphVisibility(quoteId: string) {
  await emptyPost(`store/quote/${quoteId}/toggle-graph-visible`);
}

export async function getFinanceIrelandEstimate(amount: number) {
  return await get<FinanceEstimateResponse>(`store/loan/estimate?amount=${amount}`);
}

export async function setGrantValue(assessmentId: string, amount: number) {
  return await emptyPost(`assessments/assessment/${assessmentId}/grant/${amount}`);
}

export async function getLead({ leadId }: { leadId: string }) {
  return await getItem<Lead>(`store/public/lead`, leadId);
}

export async function getLeadByCompletedQuestionnaireId({
  completedQuestionnaireId
}: {
  completedQuestionnaireId: string;
}) {
  return await getItem<Lead>(`store/public/lead/completed-questionnaire`, completedQuestionnaireId);
}

export async function getLocalHeroesQuote(quoteId: string) {
  return await get<LocalHeroesQuote>(`store/local-heroes/quote/${quoteId}`);
}

export async function sendLocalHeroesQuote(quoteId: string) {
  return await emptyPost(`store/local-heroes/send/${quoteId}`);
}

export async function getQuoteBalance(id: string) {
  return await get<QuoteBalance>(`store/quote/${id}/balance`);
}
